import { Collapse, FormControlLabel, FormHelperText, Stack, Switch } from '@mui/material';
import {
  FLOW_DEFAULT_ENABLED_HUMAN_TASK_TOOLS,
  FlowHumanTaskTool,
  type FlowHumanTaskTools,
  FlowHumanTaskType,
} from 'flow-schema';

import { isDefined } from 'common';

import { ClinicalQuestionAssistantConfig } from './ClinicalQuestionAssistant/Config';
import { DocumentPacketGeneratorConfig } from './DocumentPacketGeneratorConfig';
import { useReactFlowViewContext } from '../../../../context/context';
import { useSelectedNodeField } from '../../../ConfigFields/useSelectedNodeField';

type ToolsConfig = NonNullable<FlowHumanTaskTools['config']>;

/**
 * Tools tab for HumanTask block configuration.
 */
export function HumanTaskToolsFormFields() {
  const { isEditable } = useReactFlowViewContext();
  const { value: taskType } = useSelectedNodeField<FlowHumanTaskType>('config.taskType');
  const { value: enabledTools, setValue: setEnabledTools } = useSelectedNodeField<
    FlowHumanTaskTool[]
  >('config.tools.enabledTools');
  const { value: toolsConfig, setValue: setToolsConfig } =
    useSelectedNodeField<ToolsConfig>('config.tools.config');

  const tools = enabledTools ?? FLOW_DEFAULT_ENABLED_HUMAN_TASK_TOOLS;

  return (
    <Stack gap={2}>
      <Stack gap={2}>
        <Stack>
          <FormControlLabel
            control={
              <Switch
                checked={tools.includes(FlowHumanTaskTool.DocumentPacketGenerator)}
                disabled={!isEditable}
                onChange={() => {
                  if (tools.includes(FlowHumanTaskTool.DocumentPacketGenerator)) {
                    setEnabledTools(
                      tools.filter(
                        (enabledTool) => enabledTool !== FlowHumanTaskTool.DocumentPacketGenerator
                      )
                    );
                    if (isDefined(toolsConfig?.documentPacketGenerator)) {
                      setToolsConfig({ ...toolsConfig, documentPacketGenerator: undefined });
                    }
                    return;
                  }
                  setEnabledTools([...tools, FlowHumanTaskTool.DocumentPacketGenerator]);
                }}
              />
            }
            label="Document Packet Generator"
          />
          <FormHelperText sx={{ marginLeft: 6 }}>
            Assemble, preview, and download clinical document packets (PDF).
          </FormHelperText>
        </Stack>
        <Collapse in={tools.includes(FlowHumanTaskTool.DocumentPacketGenerator)}>
          <DocumentPacketGeneratorConfig />
        </Collapse>
      </Stack>
      {(taskType === FlowHumanTaskType.SubmitPriorAuthorization ||
        taskType === FlowHumanTaskType.CustomSubtask) && (
        <Stack gap={2}>
          <Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={
                    taskType === FlowHumanTaskType.SubmitPriorAuthorization ||
                    tools.includes(FlowHumanTaskTool.ClinicalQuestionAssistant)
                  }
                  disabled={taskType === FlowHumanTaskType.SubmitPriorAuthorization || !isEditable}
                  onChange={() => {
                    if (taskType === FlowHumanTaskType.SubmitPriorAuthorization) {
                      return;
                    }
                    if (tools.includes(FlowHumanTaskTool.ClinicalQuestionAssistant)) {
                      setEnabledTools(
                        tools.filter(
                          (enabledTool) =>
                            enabledTool !== FlowHumanTaskTool.ClinicalQuestionAssistant
                        )
                      );
                      return;
                    }
                    setEnabledTools([...tools, FlowHumanTaskTool.ClinicalQuestionAssistant]);
                  }}
                />
              }
              label="Clinical Question Assistant"
            />
            <FormHelperText sx={{ marginLeft: 6 }}>
              Use clinical context to help answer questions encountered while completing the task.
            </FormHelperText>
          </Stack>
          {taskType === FlowHumanTaskType.SubmitPriorAuthorization ? (
            <ClinicalQuestionAssistantConfig taskType={taskType} />
          ) : (
            <Collapse in={tools.includes(FlowHumanTaskTool.ClinicalQuestionAssistant)}>
              <ClinicalQuestionAssistantConfig taskType={taskType} />
            </Collapse>
          )}
        </Stack>
      )}
    </Stack>
  );
}
